import React, {useState} from 'react'
import {Box, Link, Stack, Typography} from '@mui/material'
import WbTextField from '../../components/common/WbTextField'
import WbOutlineButton from '../../components/common/WbOutlineButton'
import {
  ATTRIBUTE_KEYS,
  PARTICIPANT_TYPES,
} from '../../utils/skipLogic/conditions'
import {personaInputs} from '../../utils/skipLogic/preview'

// Chooses who the preview runs as, before the survey starts. It only asks for
// the participant type and the attributes any rule actually references; a
// generic respondent (Skip) leaves participant/attribute conditions "unknown".
// Calls onStart(persona) with { participantType?, attributes: {key: value} }.
export default function PersonaPicker({items, onStart}) {
  const {usesParticipantType, attributeKeys} = personaInputs(items)
  const [participantType, setParticipantType] = useState('')
  const [attributes, setAttributes] = useState({})

  // Nothing in this survey routes on who the respondent is.
  if (!usesParticipantType && attributeKeys.length === 0) {
    return (
      <Box className="persona-picker" sx={{p: 3, maxWidth: 420}}>
        <Typography variant="h6" sx={{mb: 1}}>
          Preview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          This survey has no participant-based rules, so you can preview as any
          respondent.
        </Typography>
        <WbOutlineButton
          CustomOutlineButtonText="Start preview"
          onClick={() => onStart({})}
        />
      </Box>
    )
  }

  const setAttr = (key, value) => setAttributes(a => ({...a, [key]: value}))
  const start = () =>
    onStart({
      participantType: participantType || undefined,
      attributes: {...attributes},
    })

  return (
    <Box className="persona-picker" sx={{p: 3, maxWidth: 420}}>
      <Typography variant="h6" sx={{mb: 1}}>
        Preview as…
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
        Choose who to preview as, so skip logic routes the way it would for them.
      </Typography>
      <Stack spacing={2}>
        <WbTextField
          select
          SelectProps={{native: true}}
          InputLabelProps={{shrink: true}}
          label="Participant type"
          value={participantType}
          onChange={e => setParticipantType(e.target.value)}
        >
          <option value="">(any / unknown)</option>
          {PARTICIPANT_TYPES.map(p => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </WbTextField>

        {attributeKeys.map(key => {
          const spec = ATTRIBUTE_KEYS.find(k => k.key === key)
          const label = spec?.label || key
          if (spec && !spec.free && Array.isArray(spec.values)) {
            return (
              <WbTextField
                key={key}
                select
                SelectProps={{native: true}}
                InputLabelProps={{shrink: true}}
                label={label}
                value={attributes[key] || ''}
                onChange={e => setAttr(key, e.target.value)}
              >
                <option value="">(unknown)</option>
                {spec.values.map(v => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </WbTextField>
            )
          }
          return (
            <WbTextField
              key={key}
              label={label}
              value={attributes[key] || ''}
              onChange={e => setAttr(key, e.target.value)}
            />
          )
        })}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{mt: 3}}>
        <WbOutlineButton CustomOutlineButtonText="Start preview" onClick={start} />
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => onStart({})}
        >
          Skip — preview as a generic respondent
        </Link>
      </Stack>
    </Box>
  )
}
